/**
 * Context-aware occupation resolution.
 *
 * The title-only resolver fails on a whole class of real profiles, and the
 * failure is loud: a 22-year technology delivery leader whose current title is
 * "Associate Director" resolved to *Brokerage Clerks*, because "associate" and
 * "director" are rank words that happen to collide with a title index entry.
 *
 * The information was right there and unused:
 *
 *     Cognizant · 21 years 10 months
 *     Associate Director                 <- all we looked at
 *     Senior Project Manager             <- ignored
 *     Service Delivery Manager, RPA Automation champion, Uipath, cloud
 *     Manager-Projects                   <- ignored
 *     Service Manager (Incident, Service and Problem management)
 *
 * So: generate candidates from the WHOLE history, then re-rank them against the
 * evidence — descriptions, skills, prior titles — matched to each candidate
 * occupation's own O*NET task text. Titles say what someone is called.
 * Descriptions say what they do.
 */

// Words that describe rank or org position and carry no occupational meaning.
const RANK_ONLY = new Set([
  "associate", "director", "manager", "senior", "junior", "lead", "principal",
  "head", "chief", "officer", "executive", "vice", "president", "vp", "deputy",
  "assistant", "staff", "global", "regional", "group", "general", "specialist",
  "professional", "consultant", "member", "partner", "level", "grade",
  "i", "ii", "iii", "iv", "v", "1", "2", "3", "4", "5",
]);

const STOP = new Set(["of", "and", "the", "for", "in", "a", "an", "to", "at", "on", "with", "&", "-"]);

// How many occupations the task-side pass may admit that no title named.
const RETRIEVE_LIMIT = 8;

// A domain-qualified reading is strong evidence, but it is still an inference
// about the person rather than something they wrote. It counts, at a discount.
const DOMAIN_WEIGHT = 0.8;


/**
 * Domain qualifiers.
 *
 * O*NET's alternate-title index already knows that "technology project manager"
 * is 15-1299.09 and "construction project manager" is 11-9021.00. What it cannot
 * know is that THIS person's bare "Project Manager" means the first one. The
 * résumé does: it is saturated with cloud, DevOps, Java and architecture.
 *
 * So when a title is generic and the evidence names a domain, we ask the index
 * the qualified question too. This is not fuzzy matching — every code it can
 * produce is one O*NET itself files under that qualified title. It is the step a
 * human recruiter does without noticing: "project manager, in what?"
 */
const DOMAINS = [
  { key: "technology",
    qualifiers: ["information technology", "software", "technology"],
    terms: ["software", "cloud", "devops", "agile", "scrum", "java", "python", "api", "database",
            "application", "applications", "platform", "cyber", "cybersecurity", "digital", "technology",
            "technologies", "architect", "architecture", "programming", "developer", "kubernetes",
            "azure", "aws", "microservices", "automation", "itil", "incident", "middleware",
            "integration", "saas", "data", "analytics", "machine", "learning"] },
  { key: "construction",
    qualifiers: ["construction"],
    terms: ["construction", "site", "contractor", "civil", "structural", "concrete", "building",
            "subcontractor", "scaffold", "survey"] },
  { key: "healthcare",
    qualifiers: ["clinical", "medical"],
    terms: ["clinical", "patient", "hospital", "nursing", "medical", "healthcare", "diagnosis",
            "therapy", "pharmacy"] },
  { key: "finance",
    qualifiers: ["financial"],
    terms: ["financial", "finance", "banking", "investment", "portfolio", "trading", "audit",
            "accounting", "underwriting", "treasury"] },
  { key: "manufacturing",
    qualifiers: ["manufacturing", "industrial"],
    terms: ["manufacturing", "production", "factory", "assembly", "lean", "six", "sigma",
            "supply", "warehouse", "logistics"] },
  { key: "marketing",
    qualifiers: ["marketing"],
    terms: ["marketing", "brand", "campaign", "advertising", "seo", "content", "audience",
            "creative", "media"] },
];

const DOMAIN_MIN_HITS = 3;

/** The domain the evidence most clearly names, or null if it names none. */
export function detectDomain(evidence) {
  let best = null;
  for (const d of DOMAINS) {
    const hits = d.terms.reduce((n, t) => n + (evidence.has(t) ? 1 : 0), 0);
    if (hits >= DOMAIN_MIN_HITS && (!best || hits > best.hits)) best = { ...d, hits };
  }
  return best;
}

/** A title carrying no occupational signal on its own. */
export function isRankOnly(title) {
  const words = String(title || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/)
    .filter((w) => w && !STOP.has(w));
  if (!words.length) return true;
  return words.every((w) => RANK_ONLY.has(w));
}

const tokenise = (s) =>
  String(s || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/)
    .filter((w) => w.length > 2 && !STOP.has(w) && !RANK_ONLY.has(w));

/**
 * Resolve a profile to an occupation using every signal available.
 *
 * @param {object} parsed      output of parseLinkedIn
 * @param {object} resolver    the title resolver
 * @param {object} taskIndex   tasks.json — code -> [{task}]
 * @param {string} extraContext  anything the user has added to clarify
 */
/**
 * Document frequency across occupations, built once and cached.
 *
 * Without this, "manage", "project" and "service" — which appear in most
 * management occupations — swamp the words that actually discriminate:
 * "incident", "automation", "cloud", "reconciliation". Same failure mode as the
 * title resolver's first version, one layer up.
 */
const indexCache = new Map();   // key -> { df, N, tokens, norm, of }

/**
 * Build an IDF + token-set index over one text source, keyed so we can hold
 * several at once (task text and DWA text are separate channels, ADR-032).
 */
function buildIndex(key, byCode, textOf) {
  if (indexCache.has(key)) return indexCache.get(key);
  const df = new Map();
  const tokens = new Map();
  const codes = Object.keys(byCode);
  for (const code of codes) {
    const seen = new Set(tokenise(textOf(byCode[code] || [])));
    tokens.set(code, seen);
    for (const w of seen) df.set(w, (df.get(w) || 0) + 1);
  }
  const N = codes.length || 1;
  const of = (w) => Math.log(1 + N / (1 + (df.get(w) || 0)));

  // Vector norm per occupation, so similarity can be normalised on BOTH sides.
  // Without it, "how much of the evidence did this occupation match?" rewards
  // whichever occupation has the widest vocabulary — which is why the task-side
  // pass first surfaced Natural Sciences Managers and Coaches and Scouts for a
  // technology delivery leader. Those aren't better matches; they are bigger nets.
  const norm = new Map();
  for (const [code, seen] of tokens) {
    let sq = 0;
    for (const w of seen) { const i = of(w); sq += i * i; }
    norm.set(code, Math.sqrt(sq) || 1);
  }

  const built = { df, N, tokens, norm, of };
  indexCache.set(key, built);
  return built;
}

const TOP_TASKS = 12;
const taskText = (rows) => rows.slice(0, TOP_TASKS).map((t) => t.task || t).join(" ");
const dwaText = (rows) => rows.join(" ");

/**
 * IDF-weighted cosine between the evidence bag and one occupation's task text.
 *
 * Rare shared terms dominate (idf squared); both sides are normalised, so a
 * broad-vocabulary occupation cannot win by sheer surface area.
 */
function similarity(evidence, code, idx, evidenceNorm) {
  const taskWords = idx.tokens.get(code);
  if (!taskWords) return { sim: 0, overlap: 0, rare: 0, hits: [] };
  let dot = 0, overlap = 0, rare = 0;
  const hits = [];
  for (const w of evidence) {
    if (!taskWords.has(w)) continue;
    overlap++;
    const i = idx.of(w);
    dot += i * i;
    if (i > 3) { rare++; hits.push(w); }   // the discriminating matches, for display
  }
  return { sim: dot / (evidenceNorm * (idx.norm.get(code) || 1)), overlap, rare, hits };
}

export function resolveWithContext(parsed, resolver, taskIndex, extraContext = "", dwaIndex = null) {
  const idf = buildIndex("tasks", taskIndex, taskText);
  const dwa = dwaIndex ? buildIndex("dwas", dwaIndex, dwaText) : null;
  const roles = parsed.roles || [];
  // ADR-030, enforced rather than merely stated: `extractTitlesFromProse` returns
  // RECOGNITION STRENGTH, not recency, and "any caller applying a recency curve
  // to that list is asserting something the document never said." This file was
  // one of the two callers doing exactly that. The parser now declares which kind
  // of list it produced, and we believe it.
  const ordered = parsed.orderedByRecency !== false;
  // With no recency to read there is no "current" role, and nothing downstream may
  // pretend otherwise. What we can still ask is whether the document names ANY
  // real occupation — see `rankOnlyNow` below.
  const current = ordered ? roles[0] || null : null;

  // 0. The evidence bag comes first, because it decides which QUESTIONS we ask
  //    of the title index below, not just how we rank the answers.
  const evidenceText = [
    parsed.taskText || "",
    (parsed.skills || []).join(" "),
    roles.map((r) => r.title).join(" "),
    extraContext || "",
  ].join(" ");
  const evidence = new Set(tokenise(evidenceText));
  const domain = detectDomain(evidence);

  // 1. Candidates from EVERY title in the history, not just the current one.
  //    A rank-only current title is exactly when history matters most.
  const candidates = new Map(); // code -> { code, title, titleScore, sources[] }
  const consider = (m, contribution, sourceTitle) => {
    const prev = candidates.get(m.code);
    if (prev) {
      prev.titleScore += contribution;
      if (!prev.sources.includes(sourceTitle)) prev.sources.push(sourceTitle);
    } else {
      candidates.set(m.code, {
        code: m.code, title: m.title, titleScore: contribution, sources: [sourceTitle],
      });
    }
  };

  roles.forEach((r, i) => {
    if (!r.title) return;
    // Current role weighs most — but ONLY when the list is actually in date order.
    const recency = ordered ? 1 / (1 + i * 0.6) : 1;
    const rankPenalty = isRankOnly(r.title) ? 0.15 : 1;
    const res = resolver.resolve(r.title, { limit: 6 });
    for (const m of res.matches) consider(m, m.confidence * recency * rankPenalty, r.title);

    // 1b. Ask the qualified question. "Project Manager" in a profile full of
    //     cloud and Java is an IT project manager, and O*NET files that title
    //     under a code the bare string cannot reach.
    if (!domain) return;
    for (const q of domain.qualifiers) {
      const qualified = `${q} ${r.title}`;
      const qres = resolver.resolve(qualified, { limit: 4 });
      // Only accept a qualified reading that the index recognises outright.
      if (qres.via !== "exact" && qres.confidence < 0.8) continue;
      for (const m of qres.matches) {
        consider(m, m.confidence * recency * DOMAIN_WEIGHT, `${r.title} (${domain.key})`);
      }
    }
  });

  // 2b. Retrieval from the TASK side.
  //
  //     Steps 1 and 3 together can only re-rank occupations that some title in
  //     the profile already named. That ceiling is invisible until it bites, and
  //     it bit hard: the title index maps bare "project manager" to exactly
  //     three codes — Architectural and Engineering Managers, Managers All
  //     Other, Project Management Specialists — with the architecture one first.
  //     *Information Technology Project Managers* (15-1299.09) is in the
  //     dataset, but is reachable only from a title string that spells out the
  //     domain ("IT project manager", "software project manager"). A twenty-year
  //     technology delivery leader called "Associate Director" names none of
  //     those, so the correct occupation was never a candidate — no amount of
  //     re-ranking, added user context, or model assistance could reach it,
  //     because every one of those mechanisms chooses from this list.
  //
  //     So we also go the other way: query every occupation's task text with the
  //     person's evidence and admit the best matches as candidates in their own
  //     right. Titles say what someone is called; this pass asks only what they
  //     did. Retrieved candidates carry titleScore 0, so they must win on
  //     evidence alone — which is exactly the bar they should clear.
  const retrieved = new Set();
  const normFor = (idx) =>
    Math.sqrt([...evidence].reduce((s2, w) => { const i = idx.of(w); return s2 + i * i; }, 0)) || 1;
  const evidenceNorm = normFor(idf);
  const dwaNorm = dwa ? normFor(dwa) : 1;

  // One score per candidate, from both channels. DWA text is domain-neutral by
  // construction, so it carries more weight than raw task text — which mixes
  // what the person does with who they do it for.
  const combined = (code) => {
    const t = similarity(evidence, code, idf, evidenceNorm);
    if (!dwa) return { ...t, sim: t.sim };
    const d = similarity(evidence, code, dwa, dwaNorm);
    const hits = [...new Set([...d.hits, ...t.hits])];
    return {
      sim: 0.4 * t.sim + 0.6 * d.sim,
      overlap: t.overlap,
      rare: d.rare,                       // discrimination judged on the clean channel
      hits,
    };
  };
  if (evidence.size) {
    const byEvidence = [];
    for (const [code] of idf.tokens) {
      if (candidates.has(code)) continue;
      const { sim, rare } = combined(code);
      // Require genuinely discriminating overlap. Generic managerial vocabulary
      // ("project", "resource", "team") matches half the dataset and is how the
      // first version of the title resolver went wrong one layer down.
      if (rare >= 2) byEvidence.push({ code, sim });
    }
    byEvidence.sort((a, b) => b.sim - a.sim);
    for (const { code } of byEvidence.slice(0, RETRIEVE_LIMIT)) {
      retrieved.add(code);
      candidates.set(code, {
        code,
        title: resolver.titleFor ? resolver.titleFor(code) : code,
        titleScore: 0,
        sources: [],
        fromEvidence: true,
      });
    }
  }

  // Only NOW can we say there is nothing to work with. Bailing out before
  // retrieval meant a Word CV — which yields no structured role list at all,
  // only prose — returned "none" every time, however much the prose said. The
  // title-less document is precisely the case retrieval was built for; checking
  // for candidates before running it made the pass unreachable for that path.
  if (!candidates.size) {
    return { matches: [], via: "none", confidence: 0, evidence: [], evidenceTerms: [...evidence].slice(0, 40) };
  }

  // 3. Re-rank candidates by how well their O*NET tasks match that evidence.

  const scored = [...candidates.values()].map((c) => {
    const { sim, overlap, hits } = combined(c.code);
    return { ...c, contextScore: sim, overlap, hits: hits.slice(0, 6) };
  });

  const maxTitle = Math.max(...scored.map((s) => s.titleScore), 0.0001);
  const maxCtx = Math.max(...scored.map((s) => s.contextScore), 0.0001);

  // When the current title is rank-only, trust context far more than the title.
  //
  // For a list with no recency, "the current title" does not exist — but reading
  // that as "no title signal at all" is too harsh, and measurably so: it dropped a
  // technology delivery leader from Information Technology Project Managers to
  // *Online Merchants* by handing the whole decision to a bag of prose words. The
  // honest question for an unordered list is whether ANY title in it names an
  // occupation.
  const rankOnlyNow = ordered
    ? (current ? isRankOnly(current.title) : true)
    : (roles.length ? roles.every((r) => isRankOnly(r.title)) : true);
  const wTitle = rankOnlyNow ? 0.3 : 0.6;
  const wCtx = 1 - wTitle;

  // Relative score — used only for ORDERING the list.
  const ranked = scored
    .map((s) => ({
      ...s,
      rank: (s.titleScore / maxTitle) * wTitle + (s.contextScore / maxCtx) * wCtx,
      why: s.hits,
    }))
    .sort((a, b) => b.rank - a.rank);

  const top = ranked[0];

  // ── Confidence is ABSOLUTE, not relative ────────────────────────────────
  // Normalising against the best candidate made the winner score ~0.98 by
  // construction — "best of this bunch" masquerading as "good match". That is
  // how a 22-year technology delivery leader was reported as Architectural and
  // Engineering Managers at 98% on the evidence "project, projects, resource",
  // with Nuclear Engineers as runner-up. Confidence that cannot be low is not
  // confidence, and a system that is confidently wrong never asks for help —
  // which is strictly worse than being wrong and asking.
  //
  // Three independent caps. The lowest wins.
  const discriminating = (top.why || []).length;          // rare terms actually matched
  const gap = ranked[1] ? top.rank - ranked[1].rank : 1;  // separation from runner-up

  // 1. Evidence strength: how much genuinely discriminating overlap exists.
  //    The tiers are set by what clearing 0.7 MEANS: we will not ask. The cost of
  //    asking is one interaction; the cost of asserting a wrong occupation is
  //    the user's trust in every number that follows it — the "POOR" verdict.
  //    Those costs are wildly asymmetric, so the bar for staying silent is high.
  //    Three shared rare words used to clear it, which is how a technology
  //    programme manager delivering systems FOR Medicare was told, without being
  //    asked, that he was a Social and Community Service Manager: "federal",
  //    "agency" and "eligibility" are his client's vocabulary, and three of them
  //    were enough. They are not enough.
  const evidenceCap = discriminating >= 6 ? 0.92
                    : discriminating >= 4 ? 0.72
                    : discriminating >= 2 ? 0.55
                    : 0.35;

  // 2. Separation: a photo-finish means we cannot tell these apart.
  const gapCap = gap >= 0.25 ? 0.95 : gap >= 0.12 ? 0.75 : 0.55;

  // 3. Title informativeness: a rank-only title carries no occupational signal
  //    at all, so we can never be confident from text alone. Cap below the
  //    escalation threshold so these ALWAYS reach a human or the model.
  const titleCap = rankOnlyNow ? 0.6 : 0.95;

  // 4. Provenance: did any title the person actually wrote produce this answer?
  //
  //    The task-side pass earns its place by surfacing occupations no title
  //    named — and pays for it by matching on whatever vocabulary is dense in
  //    the document, which is not always occupational. A technology programme
  //    manager delivering systems FOR Medicare and Medicaid resolved to *Social
  //    and Community Service Managers* at 0.75 and did not escalate, on the
  //    evidence "federal, agency, eligibility". Those are his CLIENT's words,
  //    not his job. He had six titles in his history and none of them produced
  //    that occupation.
  //
  //    A match that exists only because we inferred it from prose is a proposal,
  //    not a finding. Cap it below the escalation threshold so it is always put
  //    to the model or the person. Retrieval should widen the field it chooses
  //    from; it should never get to close the question by itself.
  const provenanceCap = top.fromEvidence ? 0.6 : 0.95;

  const confidence = Math.min(evidenceCap, gapCap, titleCap, provenanceCap);

  return {
    // Per-match confidence must SEPARATE the options, not flatten them. Clamping
    // each one to the overall cap made every row in the refiner read the same
    // percentage — five candidates at 57%, which tells the user nothing and
    // looks like the number is decorative. Scale by standing instead: the leader
    // carries the cap, the rest fall away in proportion to how far behind they
    // actually are.
    matches: ranked.slice(0, 5).map((m) => ({
      ...m,
      confidence: Math.round(confidence * (m.rank / (top.rank || 1)) * 100) / 100,
      fromEvidence: Boolean(m.fromEvidence),
    })),
    via: rankOnlyNow ? "context" : "title+context",
    confidence,
    rankOnlyTitle: rankOnlyNow,
    domain: domain ? { key: domain.key, hits: domain.hits } : null,
    // Surfaced so the UI can say WHY it is unsure, rather than just being unsure.
    diagnostics: { discriminating, gap: Math.round(gap * 100) / 100, evidenceCap, gapCap, titleCap, provenanceCap,
                   retrieved: retrieved.size, titleCandidates: candidates.size - retrieved.size },
    evidenceTerms: [...evidence].slice(0, 40),
  };
}
